import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent } from '@sentry/react';

const BREADCRUMB_VALUE_LIMIT = 200;
const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'set-cookie']);
const SENSITIVE_DATA_KEYS = new Set(['authorization', 'cookie', 'setcookie']);
const PAYLOAD_KEYS = new Set(['body', 'data', 'form', 'formdata', 'payload', 'postdata', 'requestbody', 'requestdata']);

function removeSensitiveHeaders(headers: Record<string, string> | undefined): void {
  if (!headers) return;

  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADER_NAMES.has(key.toLowerCase())) {
      delete headers[key];
    }
  }
}

function isSensitiveDataKey(key: string): boolean {
  const normalizedKey = key.replace(/[^a-z]/gi, '').toLowerCase();
  return PAYLOAD_KEYS.has(normalizedKey) || SENSITIVE_DATA_KEYS.has(normalizedKey);
}

function truncateBreadcrumbData(
  data: Record<string, unknown> | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!data) return undefined;

  const scrubbed: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveDataKey(key)) continue;
    if (typeof value === 'string') {
      scrubbed[key] = value.slice(0, BREADCRUMB_VALUE_LIMIT);
    } else if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

export function scrubErrorEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    removeSensitiveHeaders(event.request.headers);
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb: Breadcrumb) => ({
      ...breadcrumb,
      data: truncateBreadcrumbData(breadcrumb.data),
    }));
  }

  return event;
}

export function initErrorMonitoring(): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: scrubErrorEvent,
  });
}
